import { useEffect, useRef } from "react";

import {
  DEVICE_ORIENTATION_EVENT,
  deviceOrientationFrom,
  type DeviceOrientation,
} from "@/app/nativeOrientation";

// PLAN §"Racing V2 interface" → Mobile orientation contract, rule (2):
// rotating to landscape on the race page auto-enters fullscreen; rotating
// back exits ONLY when rotation entered it. Phones only — tablets and
// desktop windows never auto-fullscreen.
export const PHONE_VIEWPORT_MAX_SHORT_EDGE = 500;

export type RotationFullscreenState = {
  /** Last orientation this watcher observed; null until the mount seed. */
  orientation: DeviceOrientation | null;
  /** True while the CURRENT fullscreen was entered by a rotation. */
  autoEntered: boolean;
};

export const initialRotationFullscreenState: RotationFullscreenState = {
  orientation: null,
  autoEntered: false,
};

export type RotationFullscreenSignal =
  | {
      type: "orientation";
      orientation: DeviceOrientation;
      phoneViewport: boolean;
      isFullscreen: boolean;
    }
  | { type: "fullscreen-exited" };

export type RotationFullscreenEffect =
  "enter-fullscreen" | "exit-fullscreen" | null;

export function isPhoneViewport(width: number, height: number): boolean {
  return Math.min(width, height) <= PHONE_VIEWPORT_MAX_SHORT_EDGE;
}

export function viewportOrientation(
  width: number,
  height: number,
): DeviceOrientation {
  return width > height ? "landscape" : "portrait";
}

/**
 * Pure step of the auto-fullscreen contract.
 *
 * - portrait → landscape on a phone viewport, not fullscreen: enter
 *   (fallback fullscreen — rotation is not a user gesture, so the
 *   permissioned Fullscreen API is never attempted).
 * - landscape → portrait while an auto-entered fullscreen is active: exit.
 * - A manually entered fullscreen is never auto-exited; any exit clears
 *   the auto-entered mark.
 * - Only TRANSITIONS act: the mount seed (null → x) and repeated signals
 *   from overlapping sources (media query, resize, native sensor events)
 *   are recorded but never trigger effects.
 */
export function rotationFullscreenStep(
  state: RotationFullscreenState,
  signal: RotationFullscreenSignal,
): { state: RotationFullscreenState; effect: RotationFullscreenEffect } {
  if (signal.type === "fullscreen-exited") {
    return { state: { ...state, autoEntered: false }, effect: null };
  }
  const previous = state.orientation;
  const next: RotationFullscreenState = {
    ...state,
    orientation: signal.orientation,
  };
  if (signal.orientation === previous) {
    return { state: next, effect: null };
  }
  if (
    signal.orientation === "landscape" &&
    previous === "portrait" &&
    signal.phoneViewport &&
    !signal.isFullscreen
  ) {
    return {
      state: { ...next, autoEntered: true },
      effect: "enter-fullscreen",
    };
  }
  if (
    signal.orientation === "portrait" &&
    state.autoEntered &&
    signal.isFullscreen
  ) {
    return {
      state: { ...next, autoEntered: false },
      effect: "exit-fullscreen",
    };
  }
  return { state: next, effect: null };
}

/**
 * Rotation watcher for the race viewer. Listens to the browser orientation
 * media query + resize (mobile web, Playwright viewport rotation) and to the
 * native `mt:deviceorientation` sensor events (the shells stay
 * portrait-locked outside fullscreen, so no web resize ever fires there).
 * All sources funnel into `rotationFullscreenStep`, which dedupes them.
 */
export function useRotationAutoFullscreen({
  isFullscreen,
  onAutoEnter,
  onAutoExit,
}: {
  isFullscreen: boolean;
  onAutoEnter: () => void;
  onAutoExit: () => void;
}): void {
  const stateRef = useRef(initialRotationFullscreenState);
  const liveRef = useRef({ isFullscreen, onAutoEnter, onAutoExit });
  useEffect(() => {
    liveRef.current = { isFullscreen, onAutoEnter, onAutoExit };
  });

  const wasFullscreenRef = useRef(isFullscreen);
  useEffect(() => {
    if (wasFullscreenRef.current && !isFullscreen) {
      stateRef.current = rotationFullscreenStep(stateRef.current, {
        type: "fullscreen-exited",
      }).state;
    }
    wasFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  useEffect(() => {
    const signal = (orientation: DeviceOrientation) => {
      const { state, effect } = rotationFullscreenStep(stateRef.current, {
        type: "orientation",
        orientation,
        phoneViewport: isPhoneViewport(window.innerWidth, window.innerHeight),
        isFullscreen: liveRef.current.isFullscreen,
      });
      stateRef.current = state;
      if (effect === "enter-fullscreen") liveRef.current.onAutoEnter();
      else if (effect === "exit-fullscreen") liveRef.current.onAutoExit();
    };

    // Seed the current orientation so the FIRST rotation is a transition;
    // arriving already-landscape never auto-enters.
    signal(viewportOrientation(window.innerWidth, window.innerHeight));

    const media = window.matchMedia("(orientation: landscape)");
    const onMediaChange = (event: MediaQueryListEvent) => {
      signal(event.matches ? "landscape" : "portrait");
    };
    const onResize = () => {
      signal(viewportOrientation(window.innerWidth, window.innerHeight));
    };
    const onNativeOrientation = (event: Event) => {
      const orientation = deviceOrientationFrom(event);
      if (orientation) signal(orientation);
    };

    media.addEventListener("change", onMediaChange);
    window.addEventListener("resize", onResize);
    window.addEventListener(DEVICE_ORIENTATION_EVENT, onNativeOrientation);
    return () => {
      media.removeEventListener("change", onMediaChange);
      window.removeEventListener("resize", onResize);
      window.removeEventListener(DEVICE_ORIENTATION_EVENT, onNativeOrientation);
    };
  }, []);
}
